import {
  CheckIcon,
  DefaultLogo,
  V2Card,
  V2Heading,
  V2LinkButton,
  V2Tag,
  V2TagGroup,
} from "@stacklet/ui";
import { graphql, useFragment } from "react-relay";

import { identityProviderPath } from "./constants";

import type { IdentityProviderCard_provider$key } from "./__generated__/IdentityProviderCard_provider.graphql";

/** Props for the IdentityProviderCard component */
interface Props {
  /** Fragment reference for the SAML provider the card describes */
  providerRef: IdentityProviderCard_provider$key;
}

/**
 * A single configured identity provider, rendered as a card: the provider's
 * display name with its Cognito provider name and stable namespace, the
 * capabilities it grants, and a link to its details.
 *
 * Every SAML provider federates console logins, so the "SSO Login" capability
 * is always present; "Single Sign-Out" reflects the provider's `enableSignout`
 * flag and is omitted when the Cognito signout flow is off.
 */
export function IdentityProviderCard({ providerRef }: Props) {
  const provider = useFragment(
    graphql`
      fragment IdentityProviderCard_provider on SAMLProvider {
        name
        displayName
        enableSignout
        idpNamespace
      }
    `,
    providerRef,
  );

  const title = provider.displayName || provider.name;

  return (
    <V2Card className="flex flex-col gap-y-sm border border-overlay-medium p-sm">
      <div className="flex items-center justify-between gap-md">
        <div className="flex min-w-0 items-center gap-x-xs">
          <DefaultLogo className="shrink-0" size={24} />
          <V2Heading className="truncate" level={3}>
            {title}
          </V2Heading>
          {provider.displayName ? (
            <span className="truncate text-label-small text-text-secondary">
              {provider.name}
            </span>
          ) : null}
          <span className="shrink-0 font-mono text-label-extra-small text-text-secondary">
            {provider.idpNamespace}
          </span>
        </div>

        <V2LinkButton
          className="shrink-0"
          href={identityProviderPath(provider.name)}
          variant="tertiary"
        >
          View Details
        </V2LinkButton>
      </div>

      <V2TagGroup aria-label={`${title} capabilities`} tagListClassName="flex">
        <V2Tag color="green" icon={<CheckIcon size={14} />} id="sso-login">
          SSO Login
        </V2Tag>
        {provider.enableSignout ? (
          <V2Tag color="green" icon={<CheckIcon size={14} />} id="signout">
            Single Sign-Out
          </V2Tag>
        ) : null}
      </V2TagGroup>
    </V2Card>
  );
}
